import React, { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Chip, InputText, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { GraphHeader } from "../../../components/graphs/GraphHeader"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import { transformFeatureKeys } from "../helpers"
import type { FeatureProfilingProps, InsightProps, Profile, ProfileCardProps } from "../interfaces"

/**
 * ProfileCard component to display individual profile information.
 *
 * @param props - The properties containing profile data such as antecedents, confidence, lift, etc.
 * @returns A React element representing a profile card with the relevant details.
 */
function ProfileCard(props: Readonly<ProfileCardProps>): React.ReactElement {
  const { antecedents, confidence, label, lift, name, support } = props

  const { t } = useTranslation("insights", {
    keyPrefix: "FeatureProfiling.profileCard",
  })

  return (
    <Grid container item xs={12} className={"charts-paper-darker"} p={1} gap={1}>
      <Grid item xs={12}>
        <Typography variant="h3-bold">{name}</Typography>
      </Grid>

      <Grid container item xs={12} gap={1}>
        {/* Display profile metrics (Lift, Confidence, Support) in Chips */}
        <Grid item>
          <Chip>
            <Typography variant="a" color="important" variantColor={2} style={{ marginRight: "4px" }}>
              {Number(lift).toFixed(2)}x
            </Typography>
            {label}{" "}
            <Typography variantColor={2} style={{ marginLeft: "4px" }}>
              {t("likelihood")}
            </Typography>
          </Chip>
        </Grid>

        <Grid item>
          <Chip>
            <Typography variant="a" color="important" variantColor={2} style={{ marginRight: "4px" }}>
              {(Number(confidence) * 100).toFixed(2)}%
            </Typography>
            <Typography variantColor={2}>{t("confidence")}</Typography>
          </Chip>
        </Grid>

        <Grid item>
          <Chip>
            <Typography variant="a" color="important" variantColor={2} style={{ marginRight: "4px" }}>
              {(Number(support) * 100).toFixed(2)}%
            </Typography>
            <Typography variantColor={2}>{t("support")}</Typography>
          </Chip>
        </Grid>
      </Grid>

      {/* Display Antecedents (conditions or features that led to the profile) */}
      <Grid container item xs={12} gap={1} display={"inline-flex"}>
        {Object.entries(antecedents).map(([key, value]) => {
          let adjustedValue = value
          // If the value is a string containing '__', format it as a range
          if (typeof value === "string" && value.includes("__")) {
            const splitValue = value.split("__")

            if (splitValue.length === 2 && !!Number(splitValue[0]) && !!Number(splitValue[1])) {
              adjustedValue = `${Number(splitValue[0]).toFixed(2)} -> ${Number(splitValue[1]).toFixed(2)}`
            }
          }

          return (
            <Grid item className={"charts-paper"} py={1} px={1.5} key={key}>
              <Grid item xs={12} wrap="nowrap">
                <Typography variant={"p"} variantColor={2} noWrap>
                  {key}
                </Typography>
                {/* Display antecedent key */}
              </Grid>

              <Grid item xs={12} wrap="nowrap">
                <Typography variant={"h3-bold"} noWrap>
                  {adjustedValue}
                </Typography>
                {/* Display adjusted value for antecedent */}
              </Grid>
            </Grid>
          )
        })}
      </Grid>
    </Grid>
  )
}

/**
 * Main component to render feature profiling insights.
 *
 * This component fetches and displays feature profiling data, including labels and reasons,
 * and allows the user to filter by specific labels and reasons.
 *
 * @param props - The properties containing insight data, such as the current insight and report context.
 * @returns A React element that displays the feature profiling information, including profile cards and pagination.
 */
export function FeaturesProfiles(props: Readonly<InsightProps>): React.ReactElement {
  const { insight, isFeedback } = props
  const { id: projectId } = useParams<{ id: string }>() // Retrieve project ID from the URL
  const { report } = useContext(ReportContext) // Retrieve report context (UUID)

  const { t } = useTranslation("insights")

  const [selectedLabels, setSelectedLabels] = useState<string[] | undefined>(undefined) // State for selected labels
  const [selectedReasons, setSelectedReasons] = useState<string[] | undefined>(undefined) // State for selected reasons

  const [page, setPage] = useState<number>(0) // State for current page number
  const [pageSize, setPageSize] = useState<number>(3) // State for number of items per page

  // Fetch feature profiling data based on project ID, report UUID, and insight UUID
  const { data: insightData, isLoading: isLoadingInsight } = useQuery<AxiosResponse<FeatureProfilingProps>, AxiosError>(
    ["feature-profiling", projectId, report?.uuid, insight.uuid, isFeedback], // Unique query key
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      // Enable the query only when required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  /**
   * Memoized function to extract metadata from the insight data, including available labels and reasons.
   *
   * @returns An object containing an array of labels and a set of unique reasons extracted from the insight data.
   */
  const insightMetaData = useMemo((): { labels: string[]; reasons: string[] } => {
    let labels: string[] = []
    const reasons = new Set<string>()

    if (isLoadingInsight) return { labels, reasons: Array.from(reasons) } // Return empty arrays while loading

    // Extract labels and reasons from the insight data
    labels = Object.keys(insightData?.data?.data ?? {})?.filter((label) => label !== "__all_labels__")

    // Extract unique reasons for each label
    labels.forEach((label) =>
      Object.keys((insightData?.data?.data[label] as Record<string, Profile[]>).reasons ?? {}).forEach((reason) =>
        reasons.add(reason),
      ),
    )

    return { labels, reasons: Array.from(reasons) }
  }, [insightData?.data?.data, isLoadingInsight])

  /**
   * Memoized function to filter and adjust data based on the selected labels and reasons.
   *
   * @returns An array of ProfileCardProps representing the filtered and adjusted profiles.
   */
  const adjustedData = useMemo(() => {
    const data: ProfileCardProps[] = []

    if (isLoadingInsight) return [] // Return empty array while loading

    const labelsToProcess = selectedLabels ?? insightMetaData.labels // Use selected labels or all available labels

    let index = 0

    if (isFeedback) {
      labelsToProcess.forEach((label) => {
        data.push(
          ...(((insightData?.data.data[label] as Profile[]) ?? []).map((item: Profile) => {
            return {
              ...item,
              antecedents: transformFeatureKeys(item.antecedents),
              name: `${t("FeatureProfiling.profile")} ${++index}`,
              label: label,
              reason: "",
            }
          }) ?? []),
        )
      })
    } else
      labelsToProcess.forEach((label) => {
        if (selectedReasons === undefined) {
          // If no reasons are selected, use all reasons for the label
          data.push(
            ...((insightData?.data.data[label] as Record<string, Profile[]>)?.["__all_reasons__"] ?? [])?.map(
              (item) => {
                return {
                  ...item,
                  antecedents: transformFeatureKeys(item.antecedents),
                  name: `${t("FeatureProfiling.profile")} ${++index}`,
                  label: label,
                  reason: t("FeatureProfiling.filters.reasons.all"),
                }
              },
            ),
          )
        } else {
          // If reasons are selected, use the selected reasons for the label
          selectedReasons?.forEach((reason) => {
            data.push(
              ...((
                (insightData?.data.data[label] as Record<string, Record<string, Profile[]>>)["reasons"]?.[reason] ?? []
              ).map((item) => {
                return {
                  ...item,
                  antecedents: transformFeatureKeys(item.antecedents),
                  name: `${t("FeatureProfiling.profile")} ${++index}`,
                  label: label,
                  reason: reason,
                }
              }) ?? []),
            )
          })
        }
      })

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightData?.data.data, insightMetaData.labels, isFeedback, isLoadingInsight, selectedLabels, selectedReasons])

  // Handler to update selected labels
  const handleLabelSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedLabels(undefined) // Reset selected labels if none are selected
      return
    }
    setSelectedLabels(selectedOptions) // Update selected labels
  }

  // Handler to update selected reasons
  const handReasonSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedReasons(undefined) // Reset selected reasons if none are selected
      return
    }
    setSelectedReasons(selectedOptions) // Update selected reasons
  }

  const emptyState = useMemo((): { title: string; subtitle?: string } | undefined => {
    if (isLoadingInsight)
      return {
        title: t("FeatureProfiling.emptyState.loading.title"),
        subtitle: t("FeatureProfiling.emptyState.loading.subtitle"),
      }

    if (insight.status !== InsightList.status.SUCCEEDED)
      return {
        title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
          defaultValue: insight.status,
        })?.toLocaleLowerCase()}`,
      }

    if (!isLoadingInsight && adjustedData.length === 0)
      return {
        title: t("FeatureProfiling.emptyState.unavailable.title"),
        subtitle: t("FeatureProfiling.emptyState.unavailable.subtitle"),
      }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedData.length, insight.status, isLoadingInsight])

  return (
    <Grid item xs={12}>
      <div className={"charts-paper"} style={{ padding: "1rem" }}>
        <GraphHeader
          title={t("FeatureProfiling.title")}
          tooltipText={<Trans>{t("FeatureProfiling.tooltip")}</Trans>}
          gutterBottom
        />

        {insight.status === InsightList.status.SUCCEEDED && (
          <Grid container item xs={12} columnSpacing={1} mb={2}>
            {/* Inputs to select labels and reasons */}
            <Grid item xs={3}>
              <InputText
                label={t(`FeatureProfiling.filters.${isFeedback ? "feedback" : "decisions"}.label`)}
                options={insightMetaData.labels}
                value={
                  selectedLabels === undefined
                    ? t(`FeatureProfiling.filters.${isFeedback ? "feedback" : "decisions"}.all`)
                    : selectedLabels?.length > 0
                      ? t(`FeatureProfiling.filters.${isFeedback ? "feedback" : "decisions"}.selected`, {
                          count: selectedLabels?.length,
                        })
                      : t(`FeatureProfiling.filters.${isFeedback ? "feedback" : "decisions"}.select`)
                }
                multipleModeProps={{
                  isActive: true,
                  selectedValues: selectedLabels,
                  setSelectedValues: (selectedOptions) => handleLabelSelect(selectedOptions as string[]),
                  allowDeselect: (selectedLabels?.length ?? 0) > 0 && !isFeedback,
                  hideChips: true,
                }}
                isLoadingOptions={isLoadingInsight}
                isSelect
                fullWidth
                hideDescription
                menuProps={{ menuMaxContent: true }}
              />
            </Grid>

            {!isFeedback && (
              <Grid item xs={3}>
                <InputText
                  label={t("FeatureProfiling.filters.reasons.label")}
                  options={insightMetaData.reasons}
                  value={
                    selectedReasons === undefined
                      ? t("FeatureProfiling.filters.reasons.all")
                      : selectedReasons?.length > 0
                        ? t("FeatureProfiling.filters.reasons.selected", { count: selectedReasons?.length })
                        : t("FeatureProfiling.filters.reasons.select")
                  }
                  multipleModeProps={{
                    isActive: true,
                    selectedValues: selectedReasons,
                    setSelectedValues: (selectedOptions) => handReasonSelect(selectedOptions as string[]),
                    allowDeselect: (selectedReasons?.length ?? 0) > 0,
                    hideChips: true,
                  }}
                  isLoadingOptions={isLoadingInsight}
                  isSelect
                  fullWidth
                  hideDescription
                  menuProps={{ menuMaxContent: true }}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Refactor to a switch */}
        <Grid container gap={1} mb={2}>
          {/* Display profile cards or empty state if no data is available */}
          {emptyState ? (
            <Grid container height={"100%"} display={"flex"} justifyContent={"center"}>
              <Grid item xs={12} display={"flex"} alignContent={"center"} justifyContent={"center"} height={550}>
                <KonanEmptyState title={emptyState.title} subTitle={emptyState.subtitle} isLoading={isLoadingInsight} />
              </Grid>
            </Grid>
          ) : (
            adjustedData.slice(page * pageSize, (page + 1) * pageSize).map((profile: ProfileCardProps) => (
              <Grid item xs={12} key={profile.name}>
                <ProfileCard {...profile} />
              </Grid>
            ))
          )}
        </Grid>

        {insight.status === InsightList.status.SUCCEEDED && (
          <Grid item xs={12}>
            <Pagination
              variant={"complex"}
              totalCount={adjustedData.length}
              page={page + 1}
              onPageChange={(page: number) => setPage(page - 1)}
              pageSize={pageSize}
              onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
            />
          </Grid>
        )}
      </div>
    </Grid>
  )
}
